import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

/**
 * Taste preference values that shape the `/for-you` recommendation query.
 *
 * Shared across the web FY surface so `TopMatch`, `MoreForYou`, and the
 * `RefineTasteCard` consumer all read the same slice. Changing any field
 * fires RTK Query tag invalidation automatically because the query args
 * shift — no manual `refetch()` calls needed.
 */
export interface PreferenceState {
  /** 0–1 float: low energy → high energy. */
  energy: number;
  /** Miles, 0.5–10 in 0.5 steps. */
  maxDistanceMiles: number;
  /** Integer 1–4 ($ → $$$$). */
  maxPrice: number;
}

const initialState: PreferenceState = {
  energy: 0.5,
  maxDistanceMiles: 5,
  maxPrice: 2,
};

const preferenceSlice = createSlice({
  name: 'preference',
  initialState,
  reducers: {
    /**
     * Merge a partial update into the current state. Unspecified fields
     * are preserved.
     */
    setPreferences(state, action: PayloadAction<Partial<PreferenceState>>) {
      if (typeof action.payload.energy === 'number' && Number.isFinite(action.payload.energy)) {
        state.energy = action.payload.energy;
      }
      if (
        typeof action.payload.maxDistanceMiles === 'number' &&
        Number.isFinite(action.payload.maxDistanceMiles)
      ) {
        state.maxDistanceMiles = action.payload.maxDistanceMiles;
      }
      if (typeof action.payload.maxPrice === 'number' && Number.isFinite(action.payload.maxPrice)) {
        state.maxPrice = action.payload.maxPrice;
      }
    },

    /** Reset every field back to its default (Energy 0.5 / Distance 5 mi / Price $$). */
    resetPreferences() {
      return initialState;
    },
  },
});

export const { setPreferences, resetPreferences } = preferenceSlice.actions;

/**
 * Narrow raw selector — one per slice — so the memoized selector only
 * recomputes when the slice reference actually changes.
 */
const selectPreferenceRaw = (state: RootState): PreferenceState => state.preference;

/**
 * Select the current taste preferences (memoized). Identity-stable across
 * unrelated store updates, which keeps RTK Query args referentially stable
 * and prevents needless refetches.
 */
export const selectTastePreferences = createSelector(
  [selectPreferenceRaw],
  (pref): PreferenceState => pref,
);

/** Expose the defaults so consumers (tests, reset buttons) don't duplicate. */
export const DEFAULT_TASTE_PREFERENCES: PreferenceState = { ...initialState };

export default preferenceSlice.reducer;
